/**
 * React hook for pipeline management
 */

import { useState, useCallback } from 'react';
import pipelineApi from '../api/client';
import type { PipelineDefinition, PipelineNode, PipelineEdge } from '../types/pipeline';
import { copyCanvasLayout } from '../utils/canvasLayout';

// crypto.randomUUID() requires a secure context (HTTPS or localhost); fall back to
// crypto.getRandomValues(), which is available everywhere, when it's missing.
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function usePipeline() {
  const [pipeline, setPipeline] = useState<PipelineDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isNewPipeline, setIsNewPipeline] = useState(true);

  const createNewPipeline = useCallback(() => {
    const newPipeline: PipelineDefinition = {
      id: generateUUID(),
      metadata: {
        name: 'New Pipeline',
        description: '',
        tags: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: '00000000-0000-0000-0000-000000000000', // Default user
      },
      nodes: [],
      edges: [],
    };
    console.log('Creating new pipeline:', { id: newPipeline.id });
    setPipeline(newPipeline);
    setIsNewPipeline(true);
    return newPipeline;
  }, []);

  const loadPipeline = useCallback(async (pipelineId: string) => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await pipelineApi.getPipeline(pipelineId);
      setPipeline(loaded);
      setIsNewPipeline(false);
      return loaded;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const savePipeline = useCallback(async () => {
    if (!pipeline) return;

    setLoading(true);
    setError(null);
    try {
      console.log('Saving pipeline:', {
        id: pipeline.id,
        isNewPipeline,
        nodeCount: pipeline.nodes.length,
        edgeCount: pipeline.edges.length,
      });

      let saved;
      if (isNewPipeline) {
        console.log('Creating new pipeline...');
        ({ saved } = await pipelineApi.createPipeline(pipeline));
        setIsNewPipeline(false);
      } else {
        console.log('Updating existing pipeline...');
        saved = await pipelineApi.updatePipeline(pipeline.id, pipeline);
      }

      console.log('Save successful');

      // Update timestamp and the saved inputs/slug/version
      setPipeline(current => current && {
        ...current,
        metadata: {
          ...current.metadata,
          ...saved,
          updated_at: new Date().toISOString(),
        },
      });
    } catch (err) {
      console.error('Save failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [pipeline, isNewPipeline]);

  const addNode = useCallback((node: PipelineNode) => {
    if (!pipeline) return;

    setPipeline({
      ...pipeline,
      nodes: [...pipeline.nodes, node],
    });
  }, [pipeline]);

  const updateNode = useCallback((nodeId: string, updates: Partial<PipelineNode>) => {
    if (!pipeline) return;

    setPipeline({
      ...pipeline,
      nodes: pipeline.nodes.map(node =>
        node.id === nodeId ? { ...node, ...updates } : node
      ),
    });
  }, [pipeline]);

  const deleteNode = useCallback((nodeId: string) => {
    if (!pipeline) return;

    setPipeline({
      ...pipeline,
      nodes: pipeline.nodes.filter(node => node.id !== nodeId),
      edges: pipeline.edges.filter(edge =>
        edge.source !== nodeId && edge.target !== nodeId
      ),
    });
  }, [pipeline]);

  const addEdge = useCallback((edge: PipelineEdge) => {
    if (!pipeline) return;

    setPipeline({
      ...pipeline,
      edges: [...pipeline.edges, edge],
    });
  }, [pipeline]);

  const deleteEdge = useCallback((edgeId: string) => {
    if (!pipeline) return;

    setPipeline({
      ...pipeline,
      edges: pipeline.edges.filter(edge => edge.id !== edgeId),
    });
  }, [pipeline]);

  const updateMetadata = useCallback((updates: Partial<PipelineDefinition['metadata']>) => {
    if (!pipeline) return;

    setPipeline({
      ...pipeline,
      metadata: {
        ...pipeline.metadata,
        ...updates,
      },
    });
  }, [pipeline]);

  // Manual setter for when loading existing pipeline from list
  // Open a copy of an existing workflow as an unsaved new one: fresh id, "(copy)" name, and its
  // slug/version dropped so pipelineToWorkflow derives a new slug from the name on first save.
  const duplicatePipeline = useCallback((source: PipelineDefinition) => {
    const now = new Date().toISOString();
    const { slug: _slug, version: _version, ...metadata } = source.metadata as PipelineDefinition['metadata'] & {
      slug?: string;
      version?: number;
    };
    const copy: PipelineDefinition = {
      ...structuredClone(source),
      id: generateUUID(),
      metadata: { ...structuredClone(metadata), name: `${source.metadata.name} (copy)`, created_at: now, updated_at: now },
    };
    copyCanvasLayout(source, copy.id);
    setPipeline(copy);
    setIsNewPipeline(true);
    return copy;
  }, []);

  const setExistingPipeline = useCallback((p: PipelineDefinition) => {
    console.log('Setting existing pipeline:', {
      id: p.id,
      name: p.metadata.name,
      nodeCount: p.nodes.length,
    });
    setPipeline(p);
    setIsNewPipeline(false);
  }, []);

  return {
    pipeline,
    setPipeline,
    setExistingPipeline,
    duplicatePipeline,
    loading,
    error,
    createNewPipeline,
    loadPipeline,
    savePipeline,
    addNode,
    updateNode,
    deleteNode,
    addEdge,
    deleteEdge,
    updateMetadata,
  };
}
